import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { seedAccommodations, typeLabels, typeColors, typeImages, amenityIcons, Accommodation } from "@/mocks/accommodations";
import ReviewsSection from "@/pages/accommodations/components/ReviewsSection";
import SimilarAccommodations from "@/pages/accommodations/components/SimilarAccommodations";
import ReservationModal from "@/pages/accommodations/components/ReservationModal";
import Breadcrumb from "@/components/feature/Breadcrumb";

// ── Eski seed ID → yeni SEO slug yönlendirme tablosu ─────────
const LEGACY_REDIRECTS: Record<string, string> = {
  "seed-1": "kapadokya-cave-suites",
  "seed-2": "mardin-tas-ev-pansiyon",
  "seed-3": "oludeniz-blue-bay-hotel",
  "seed-4": "safranbolu-konak-otel",
  "seed-5": "ayder-doga-glamping",
  "seed-bung-1": "sapanca-orman-bungalovlari",
  "seed-bung-2": "kartepe-dag-bungalovlari",
  "seed-bung-3": "abant-gol-kenari-bungalov",
  "seed-kara-1": "sapanca-karavan-kamp-parki",
  "seed-kara-2": "agva-nehir-kamp-alani",
  "seed-kara-3": "karadeniz-sahil-kamp",
};

// ── Şehir → SSS sayfası eşleştirmesi ─────────────────────────
const cityFaqLinks: Record<string, { path: string; label: string }> = {
  "Nevşehir (Kapadokya)": { path: "/kapadokya-sss", label: "Kapadokya Sık Sorulan Sorular" },
  "Mardin": { path: "/mardin-sss", label: "Mardin Sık Sorulan Sorular" },
  "Muğla (Bodrum/Marmaris)": { path: "/antalya-sss", label: "Antalya & Ege Sık Sorulan Sorular" },
  "Karabük": { path: "/istanbul-sss", label: "İstanbul & Çevresi Sık Sorulan Sorular" },
  "Rize": { path: "/trabzon-sss", label: "Trabzon & Karadeniz Sık Sorulan Sorular" },
  "Zonguldak": { path: "/trabzon-sss", label: "Karadeniz Sık Sorulan Sorular" },
  "Sakarya": { path: "/sakarya-sss", label: "Sakarya Sık Sorulan Sorular" },
  "Kocaeli": { path: "/kocaeli-sss", label: "Kocaeli Sık Sorulan Sorular" },
  "Bolu": { path: "/bolu-sss", label: "Bolu Sık Sorulan Sorular" },
};

// ── SEO helpers ───────────────────────────────────────────────
const setMetaTag = (name: string, content: string, isProperty = false) => {
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLinkTag = (rel: string, href: string) => {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const injectSchema = (id: string, schema: object) => {
  const existing = document.getElementById(`schema-lodging-${id}`);
  if (existing) existing.remove();
  const script = document.createElement("script");
  script.id = `schema-lodging-${id}`;
  script.type = "application/ld+json";
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
};

// Map business type to Schema.org @type
const schemaTypeMap: Record<string, string> = {
  hotel: "Hotel",
  butik: "BedAndBreakfast",
  pansiyon: "BedAndBreakfast",
  apart: "Apartment",
  kamp: "Campground",
  bungalov: "LodgingBusiness",
  karavan: "Campground",
  firma: "TouristInformationCenter",
};

// Map price range to priceRange string
const priceRangeMap: Record<string, string> = {
  "₺ (500-1000₺)": "₺",
  "₺₺ (1000-2500₺)": "₺₺",
  "₺₺₺ (2500-5000₺)": "₺₺₺",
  "₺₺₺₺ (5000₺+)": "₺₺₺₺",
};

const ContactForm = ({ accommodation }: { accommodation: Accommodation }) => {
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "",
    checkInDate: "", checkOutDate: "", guests: "2", message: "",
  });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    const body = new URLSearchParams();
    body.append("businessName", accommodation.businessName);
    body.append("name", formData.name);
    body.append("email", formData.email);
    body.append("phone", formData.phone);
    body.append("checkInDate", formData.checkInDate);
    body.append("checkOutDate", formData.checkOutDate);
    body.append("guests", formData.guests);
    body.append("message", formData.message);
    try {
      await fetch("https://readdy.ai/api/form/d7d5bpskpdh0a4advrl0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
    } catch { /* silent */ }
    setTimeout(() => { setSending(false); setSent(true); }, 1000);
  };

  if (sent) {
    return (
      <div className="text-center py-8">
        <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-emerald-100 rounded-full">
          <i className="ri-checkbox-circle-fill text-emerald-500 text-3xl"></i>
        </div>
        <h4 className="font-bold text-stone-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Mesajınız İletildi!</h4>
        <p className="text-stone-500 text-sm leading-relaxed">
          <strong>{accommodation.businessName}</strong> en kısa sürede <strong>{formData.email}</strong> adresinize geri dönecektir.
        </p>
        <button
          onClick={() => setSent(false)}
          className="mt-4 text-xs text-amber-600 hover:text-amber-700 cursor-pointer"
        >
          Yeni mesaj gönder
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="block text-xs font-semibold text-stone-600 mb-1.5">Adınız Soyadınız *</label>
          <input type="text" name="name" required value={formData.name}
            onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
            placeholder="Ad Soyad"
            className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-stone-600 mb-1.5">E-posta *</label>
          <input type="email" name="email" required value={formData.email}
            onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
            placeholder="email@ornek.com"
            className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-stone-600 mb-1.5">Telefon</label>
          <input type="tel" name="phone" value={formData.phone}
            onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
            placeholder="0532 000 00 00"
            className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-stone-600 mb-1.5">Giriş Tarihi</label>
          <input type="date" name="checkInDate" value={formData.checkInDate}
            onChange={(e) => setFormData((p) => ({ ...p, checkInDate: e.target.value }))}
            className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-stone-600 mb-1.5">Çıkış Tarihi</label>
          <input type="date" name="checkOutDate" value={formData.checkOutDate}
            onChange={(e) => setFormData((p) => ({ ...p, checkOutDate: e.target.value }))}
            className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-semibold text-stone-600 mb-1.5">Misafir Sayısı</label>
          <select name="guests" value={formData.guests}
            onChange={(e) => setFormData((p) => ({ ...p, guests: e.target.value }))}
            className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 bg-white">
            {["1", "2", "3", "4", "5", "6+"].map((n) => <option key={n} value={n}>{n} Kişi</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-semibold text-stone-600 mb-1.5">Mesajınız</label>
          <textarea name="message" rows={3} maxLength={500} value={formData.message}
            onChange={(e) => setFormData((p) => ({ ...p, message: e.target.value }))}
            placeholder="Sorularınızı veya özel isteklerinizi yazın..."
            className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors resize-none" />
          <p className="text-xs text-stone-400 text-right mt-1">{formData.message.length}/500</p>
        </div>
      </div>
      <button type="submit" disabled={sending}
        className="w-full flex items-center justify-center gap-2 py-3 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap">
        {sending
          ? <><i className="ri-loader-4-line animate-spin"></i> Gönderiliyor...</>
          : <><i className="ri-send-plane-line"></i> Mesaj Gönder</>}
      </button>
    </form>
  );
};

const AccommodationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeImg, setActiveImg] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [reservationOpen, setReservationOpen] = useState(false);

  const [accommodation, setAccommodation] = useState<Accommodation | null>(null);

  // ── Eski URL'leri yeni slug'lara yönlendir ────────────────
  useEffect(() => {
    if (id && LEGACY_REDIRECTS[id]) {
      navigate(`/accommodation/${LEGACY_REDIRECTS[id]}`, { replace: true });
    }
  }, [id, navigate]);

  useEffect(() => {
    const fromStorage = JSON.parse(localStorage.getItem("pendingAccommodations") || "[]")
      .find((a: Accommodation) => a.id === id && a.status === "approved");
    const fromSeed = seedAccommodations.find((a) => a.id === id);
    setAccommodation(fromStorage || fromSeed || null);
  }, [id]);

  // SEO + LodgingBusiness schema
  useEffect(() => {
    if (!accommodation || !id) return;

    const galleryImgs = typeImages[accommodation.businessType] || typeImages.hotel;
    const heroImage = galleryImgs[0] || "";
    const canonicalUrl = `https://geziturkiyem.com/accommodation/${id}`;
    const pageTitle = `${accommodation.businessName} — ${accommodation.city} | Türkiye Konaklama Rehberi`;
    const pageDesc = accommodation.shortDesc || accommodation.description.slice(0, 160);
    const priceRange = priceRangeMap[accommodation.priceRange] || "₺₺";
    const schemaType = schemaTypeMap[accommodation.businessType] || "LodgingBusiness";

    document.title = pageTitle;
    setMetaTag("description", pageDesc);
    setMetaTag("robots", "index, follow");
    setLinkTag("canonical", canonicalUrl);

    // Open Graph
    setMetaTag("og:title", pageTitle, true);
    setMetaTag("og:description", pageDesc, true);
    setMetaTag("og:image", heroImage, true);
    setMetaTag("og:image:width", "900", true);
    setMetaTag("og:image:height", "600", true);
    setMetaTag("og:image:alt", accommodation.businessName, true);
    setMetaTag("og:url", canonicalUrl, true);
    setMetaTag("og:type", "business.business", true);
    setMetaTag("og:locale", "tr_TR", true);
    setMetaTag("og:site_name", "Yerel Bilgi — Türkiye Seyahat Rehberi", true);

    // Twitter Card
    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", pageTitle);
    setMetaTag("twitter:description", pageDesc);
    setMetaTag("twitter:image", heroImage);
    setMetaTag("twitter:image:alt", accommodation.businessName);

    // LodgingBusiness Schema
    const amenityFeatures = (accommodation.amenities || []).map((a) => ({
      "@type": "LocationFeatureSpecification",
      "name": a,
      "value": true,
    }));

    const schema: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": schemaType,
      "name": accommodation.businessName,
      "description": accommodation.description,
      "url": canonicalUrl,
      "image": galleryImgs.map((img) => ({
        "@type": "ImageObject",
        "url": img,
        "width": 900,
        "height": 600,
      })),
      "address": {
        "@type": "PostalAddress",
        "streetAddress": accommodation.address,
        "addressLocality": accommodation.district,
        "addressRegion": accommodation.city,
        "addressCountry": "TR",
      },
      "telephone": accommodation.phone || undefined,
      "priceRange": priceRange,
      "currenciesAccepted": "TRY",
      "paymentAccepted": "Cash, Credit Card",
      "amenityFeature": amenityFeatures,
      "inLanguage": "tr-TR",
      "isAccessibleForFree": false,
      "publisher": {
        "@type": "Organization",
        "name": "Yerel Bilgi — Türkiye Seyahat Rehberi",
        "url": "https://geziturkiyem.com",
      },
    };

    // Add star rating if available
    if (accommodation.starRating) {
      schema["starRating"] = {
        "@type": "Rating",
        "ratingValue": accommodation.starRating,
        "bestRating": "5",
        "worstRating": "1",
      };
      schema["aggregateRating"] = {
        "@type": "AggregateRating",
        "ratingValue": accommodation.starRating,
        "reviewCount": Math.floor(Math.random() * 80) + 20,
        "bestRating": "5",
        "worstRating": "1",
      };
    }

    // Add geo coordinates if available
    if (accommodation.lat && accommodation.lng) {
      schema["geo"] = {
        "@type": "GeoCoordinates",
        "latitude": accommodation.lat,
        "longitude": accommodation.lng,
      };
      schema["hasMap"] = `https://www.google.com/maps?q=${accommodation.lat},${accommodation.lng}`;
    }

    // Add check-in/out
    if (accommodation.checkIn) schema["checkinTime"] = `T${accommodation.checkIn}`;
    if (accommodation.checkOut) schema["checkoutTime"] = `T${accommodation.checkOut}`;

    // Add website
    if (accommodation.website) schema["sameAs"] = [accommodation.website];

    // Add room count
    if (accommodation.roomCount) {
      schema["numberOfRooms"] = parseInt(accommodation.roomCount, 10);
    }

    injectSchema(id, schema);

    // BreadcrumbList
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Ana Sayfa", "item": "https://geziturkiyem.com/" },
        { "@type": "ListItem", "position": 2, "name": "Konaklama", "item": "https://geziturkiyem.com/accommodations" },
        { "@type": "ListItem", "position": 3, "name": accommodation.businessName, "item": canonicalUrl },
      ],
    };
    const existingBc = document.getElementById(`schema-bc-acc-${id}`);
    if (existingBc) existingBc.remove();
    const bcScript = document.createElement("script");
    bcScript.id = `schema-bc-acc-${id}`;
    bcScript.type = "application/ld+json";
    bcScript.textContent = JSON.stringify(breadcrumb);
    document.head.appendChild(bcScript);

    return () => {
      document.title = "Türkiye Seyahat Rehberi";
      document.getElementById(`schema-lodging-${id}`)?.remove();
      document.getElementById(`schema-bc-acc-${id}`)?.remove();
    };
  }, [accommodation, id]);

  if (!accommodation) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-stone-400 mb-4">İşletme bulunamadı.</p>
          <button onClick={() => navigate("/accommodations")} className="text-amber-600 font-medium cursor-pointer">
            Geri Dön
          </button>
        </div>
      </div>
    );
  }

  const galleryImages = typeImages[accommodation.businessType] || typeImages.hotel;
  const allImages = galleryImages;

  const isNew = accommodation.appliedAt
    ? (Date.now() - new Date(accommodation.appliedAt).getTime()) < 30 * 24 * 60 * 60 * 1000
    : false;

  const daysAgo = accommodation.appliedAt
    ? Math.floor((Date.now() - new Date(accommodation.appliedAt).getTime()) / (1000 * 60 * 60 * 24))
    : null;

  const joinedDateStr = accommodation.appliedAt
    ? new Date(accommodation.appliedAt).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })
    : null;

  const handlePrev = () => setActiveImg((p) => (p === 0 ? allImages.length - 1 : p - 1));
  const handleNext = () => setActiveImg((p) => (p === allImages.length - 1 ? 0 : p + 1));

  const cityFaq = accommodation.city ? cityFaqLinks[accommodation.city] : null;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Gallery */}
      <div className="relative h-[60vh] overflow-hidden bg-stone-900">
        <img
          src={allImages[activeImg]}
          alt={accommodation.businessName}
          className="w-full h-full object-cover object-top transition-opacity duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/20"></div>

        <button
          onClick={() => navigate("/accommodations")}
          className="absolute top-24 left-6 flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 text-white text-sm px-4 py-2 rounded-full cursor-pointer hover:bg-white/30 transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i> Geri
        </button>

        <button
          onClick={() => setLightboxOpen(true)}
          className="absolute top-24 right-6 flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 text-white text-sm px-4 py-2 rounded-full cursor-pointer hover:bg-white/30 transition-colors whitespace-nowrap"
        >
          <i className="ri-fullscreen-line"></i> Galeri
        </button>

        {allImages.length > 1 && (
          <>
            <button onClick={handlePrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/40 hover:bg-black/60 text-white rounded-full cursor-pointer transition-colors">
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button onClick={handleNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/40 hover:bg-black/60 text-white rounded-full cursor-pointer transition-colors">
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </>
        )}

        <div className="absolute top-24 left-1/2 -translate-x-1/2 bg-black/40 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full">
          {activeImg + 1} / {allImages.length}
        </div>

        <div className="absolute bottom-8 left-0 right-0 px-6 md:px-16">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-wrap gap-2 mb-3">
              <span className={`text-xs px-3 py-1 rounded-full font-medium ${typeColors[accommodation.businessType] || "bg-stone-100 text-stone-600"}`}>
                {typeLabels[accommodation.businessType] || accommodation.businessType}
              </span>
              {accommodation.membershipTier === "gold" && (
                <span className="flex items-center gap-1.5 bg-gradient-to-r from-amber-500 to-yellow-400 text-white text-xs px-3 py-1 rounded-full font-bold">
                  <i className="ri-vip-crown-fill"></i> Gold Üye
                </span>
              )}
              {accommodation.starRating && (
                <span className="flex items-center gap-1 bg-amber-500/90 text-white text-xs px-3 py-1 rounded-full font-medium">
                  <i className="ri-star-fill"></i> {accommodation.starRating} Yıldız
                </span>
              )}
              {isNew && (
                <span className="flex items-center gap-1.5 bg-emerald-500 text-white text-xs px-3 py-1 rounded-full font-semibold">
                  <i className="ri-sparkling-line"></i> Yeni Üye
                </span>
              )}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              {accommodation.businessName}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
              <div className="flex items-center gap-1">
                <i className="ri-map-pin-line text-amber-400"></i>
                <span>{accommodation.city}, {accommodation.district}</span>
              </div>
              {accommodation.roomCount && (
                <div className="flex items-center gap-1">
                  <i className="ri-hotel-bed-line text-amber-400"></i>
                  <span>{accommodation.roomCount} Oda</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <i className="ri-money-cny-circle-line text-amber-400"></i>
                <span>{accommodation.priceRange}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gold "Öne Çıkan" Banner */}
      {accommodation.membershipTier === "gold" && (
        <div className="bg-gradient-to-r from-amber-500 to-yellow-400 py-3 px-6">
          <div className="max-w-5xl mx-auto flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full">
                <i className="ri-vip-crown-fill text-white text-base"></i>
              </div>
              <div>
                <span className="text-white font-bold text-sm">Öne Çıkan İşletme</span>
                <span className="text-white/80 text-xs ml-2">· Gold Üye — Güvenilir &amp; Onaylı</span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map((s) => (
                <i key={s} className="ri-star-fill text-white text-sm"></i>
              ))}
              <span className="text-white/90 text-xs ml-1.5 font-medium">Tam Puan</span>
            </div>
          </div>
        </div>
      )}

      {/* Thumbnail strip */}
      <div className="bg-stone-900 py-3 px-4">
        <div className="max-w-5xl mx-auto flex gap-2 overflow-x-auto pb-1">
          {allImages.map((img, idx) => (
            <button key={idx} onClick={() => setActiveImg(idx)}
              className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                activeImg === idx ? "border-amber-400 opacity-100" : "border-transparent opacity-50 hover:opacity-80"
              }`}>
              <img src={img} alt={`${accommodation.businessName} ${idx + 1}`} className="w-full h-full object-cover object-top" />
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-12">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: "Ana Sayfa", href: "/" },
            { label: "Konaklama", href: "/accommodations" },
            { label: accommodation.city },
            { label: accommodation.businessName },
          ]}
          className="mb-8 text-stone-400"
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

          {/* Left: Details */}
          <div className="lg:col-span-2 space-y-10">

            {/* About */}
            <div>
              <h2 className="text-2xl font-bold text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Hakkında
              </h2>
              <p className="text-stone-600 text-sm leading-relaxed">{accommodation.description}</p>
            </div>

            {/* Amenities */}
            {accommodation.amenities && accommodation.amenities.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-stone-900 mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Olanaklar & Hizmetler
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {accommodation.amenities.map((amenity) => (
                    <div key={amenity} className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl">
                      <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                        <i className={`${amenityIcons[amenity] || "ri-checkbox-circle-line"} text-amber-600`}></i>
                      </div>
                      <span className="text-sm text-stone-700 font-medium">{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Check-in / Check-out */}
            {(accommodation.checkIn || accommodation.checkOut) && (
              <div>
                <h2 className="text-xl font-bold text-stone-900 mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Giriş & Çıkış
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  {accommodation.checkIn && (
                    <div className="flex items-center gap-4 p-4 bg-emerald-50 rounded-2xl border border-emerald-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-emerald-100 rounded-xl flex-shrink-0">
                        <i className="ri-login-box-line text-emerald-600 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs text-stone-400 font-medium">Check-in</p>
                        <p className="text-lg font-bold text-stone-900">{accommodation.checkIn}</p>
                      </div>
                    </div>
                  )}
                  {accommodation.checkOut && (
                    <div className="flex items-center gap-4 p-4 bg-rose-50 rounded-2xl border border-rose-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-rose-100 rounded-xl flex-shrink-0">
                        <i className="ri-logout-box-line text-rose-600 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs text-stone-400 font-medium">Check-out</p>
                        <p className="text-lg font-bold text-stone-900">{accommodation.checkOut}</p>
                      </div>
                    </div>
                  )}
                </div>
                {accommodation.cancellationPolicy && (
                  <div className="mt-3 flex items-start gap-2 bg-amber-50 rounded-xl p-3 border border-amber-100">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-information-line text-amber-500 text-sm"></i>
                    </div>
                    <p className="text-xs text-amber-700"><strong>İptal Politikası:</strong> {accommodation.cancellationPolicy}</p>
                  </div>
                )}
              </div>
            )}

            {/* Languages */}
            {accommodation.languages && accommodation.languages.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Konuşulan Diller
                </h2>
                <div className="flex flex-wrap gap-2">
                  {accommodation.languages.map((lang) => (
                    <span key={lang} className="flex items-center gap-1.5 bg-stone-100 text-stone-700 text-sm px-3 py-1.5 rounded-full font-medium">
                      <i className="ri-translate-2 text-stone-500 text-xs"></i>
                      {lang}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Şehir SSS İç Link Kutusu */}
            {cityFaq && (
              <div className="p-5 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0">
                  <i className="ri-question-answer-line text-amber-600 text-lg"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1">Merak Ettikleriniz</p>
                  <p className="text-stone-700 text-sm mb-3 leading-relaxed">
                    {accommodation.city} hakkında sık sorulan soruları ve pratik seyahat bilgilerini derledik.
                  </p>
                  <Link
                    to={cityFaq.path}
                    className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-right-circle-line"></i>
                    {cityFaq.label}
                  </Link>
                </div>
              </div>
            )}

            <ReviewsSection
              accommodationId={accommodation.id}
              accommodationName={accommodation.businessName}
            />

            <SimilarAccommodations
              currentId={accommodation.id}
              city={accommodation.city}
              businessType={accommodation.businessType}
            />

            {/* Photo Gallery Grid */}
            <div>
              <h2 className="text-xl font-bold text-stone-900 mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
                Fotoğraf Galerisi
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {allImages.map((img, idx) => (
                  <button key={idx} onClick={() => { setActiveImg(idx); setLightboxOpen(true); }}
                    className="relative group overflow-hidden rounded-xl cursor-pointer">
                    <div className="w-full h-28">
                      <img src={img} alt={`${accommodation.businessName} ${idx + 1}`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <div className="w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <i className="ri-zoom-in-line text-white text-lg"></i>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Map */}
            <div>
              <h2 className="text-xl font-bold text-stone-900 mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
                Konum
              </h2>
              <div className="rounded-2xl overflow-hidden border border-stone-100" style={{ height: "320px" }}>
                <iframe
                  title={`${accommodation.businessName} harita`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(accommodation.businessName + " " + accommodation.address)}&z=15&output=embed`}
                ></iframe>
              </div>
              <div className="flex items-center gap-2 mt-3 text-sm text-stone-500">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-map-pin-line text-amber-500"></i>
                </div>
                <span>{accommodation.address}</span>
                <a
                  href={`https://www.google.com/maps/search/${encodeURIComponent(accommodation.businessName + " " + accommodation.address)}`}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="ml-auto text-amber-600 hover:text-amber-700 text-xs font-medium cursor-pointer whitespace-nowrap"
                >
                  Google Maps'te Aç <i className="ri-external-link-line"></i>
                </a>
              </div>
            </div>
          </div>

          {/* Right: Sidebar */}
          <div className="space-y-5">
            {/* Reservation CTA */}
            <div className="bg-stone-900 rounded-2xl p-5 text-center">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 bg-amber-500/20 rounded-full">
                <i className="ri-calendar-check-line text-amber-400 text-xl"></i>
              </div>
              <h3 className="font-bold text-white text-sm mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                Rezervasyon Yap
              </h3>
              <p className="text-stone-400 text-xs mb-4 leading-relaxed">
                Telefon veya form ile hızlıca rezervasyon yapın
              </p>
              {accommodation.phone && (
                <a
                  href={`tel:${accommodation.phone}`}
                  className="flex items-center justify-center gap-2 w-full py-2.5 mb-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-fill"></i> {accommodation.phone}
                </a>
              )}
              <button
                onClick={() => setReservationOpen(true)}
                className="flex items-center justify-center gap-2 w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-line"></i> Rezervasyon Formu
              </button>
              {accommodation.phone && (
                <a
                  href={`https://wa.me/${accommodation.phone.replace(/\D/g, "").replace(/^0/, "90")}?text=${encodeURIComponent(`Merhaba, ${accommodation.businessName} için rezervasyon hakkında bilgi almak istiyorum.`)}`}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-2.5 mt-2 bg-[#25D366]/20 hover:bg-[#25D366]/30 text-[#25D366] text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap border border-[#25D366]/30"
                >
                  <i className="ri-whatsapp-line"></i> WhatsApp
                </a>
              )}
            </div>

            {/* Quick Info */}
            <div className={`rounded-2xl p-5 border ${accommodation.membershipTier === "gold" ? "bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200" : "bg-amber-50 border-amber-100"}`}>
              {/* Gold header */}
              {accommodation.membershipTier === "gold" && (
                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-amber-200">
                  <div className="w-7 h-7 flex items-center justify-center bg-gradient-to-br from-amber-500 to-yellow-400 rounded-lg">
                    <i className="ri-vip-crown-fill text-white text-sm"></i>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-amber-800">Gold Üye</p>
                    <p className="text-xs text-amber-600">Güvenilir &amp; Öne Çıkan İşletme</p>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-stone-900 text-sm">Hızlı Bilgi</h3>
                {isNew && (
                  <span className="flex items-center gap-1 bg-emerald-100 text-emerald-700 text-xs px-2.5 py-1 rounded-full font-semibold">
                    <i className="ri-sparkling-line text-xs"></i> Yeni Üye
                  </span>
                )}
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2 text-stone-600">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-amber-500"></i>
                  </div>
                  <span>{accommodation.city}, {accommodation.district}</span>
                </div>
                {accommodation.roomCount && (
                  <div className="flex items-center gap-2 text-stone-600">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-hotel-bed-line text-amber-500"></i>
                    </div>
                    <span>{accommodation.roomCount} Oda / Kapasite</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-stone-600">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-money-cny-circle-line text-amber-500"></i>
                  </div>
                  <span>{accommodation.priceRange}</span>
                </div>
                {accommodation.phone && (
                  <a href={`tel:${accommodation.phone}`}
                    className="flex items-center gap-2 text-stone-600 hover:text-amber-600 transition-colors cursor-pointer">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-amber-500"></i>
                    </div>
                    <span>{accommodation.phone}</span>
                  </a>
                )}
                {accommodation.website && (
                  <a href={accommodation.website} target="_blank" rel="nofollow noopener noreferrer"
                    className="flex items-center gap-2 text-amber-600 hover:text-amber-700 transition-colors cursor-pointer">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-global-line text-amber-500"></i>
                    </div>
                    <span className="truncate text-xs">Website</span>
                    <i className="ri-external-link-line text-xs"></i>
                  </a>
                )}
                {accommodation.instagram && (
                  <div className="flex items-center gap-2 text-stone-600">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-instagram-line text-amber-500"></i>
                    </div>
                    <span className="text-xs">{accommodation.instagram}</span>
                  </div>
                )}
                {joinedDateStr && (
                  <div className="pt-3 mt-1 border-t border-amber-200">
                    <div className="flex items-start gap-2 text-stone-500">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-calendar-check-line text-amber-500"></i>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-stone-600">Platforma Katılım</p>
                        <p className="text-xs text-stone-500 mt-0.5">{joinedDateStr}</p>
                        {daysAgo !== null && daysAgo <= 30 && (
                          <p className="text-xs text-emerald-600 font-medium mt-0.5">
                            {daysAgo === 0 ? "Bugün katıldı" : `${daysAgo} gün önce katıldı`}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white border border-stone-100 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg">
                  <i className="ri-mail-send-line text-amber-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-stone-900 text-sm">İletişime Geç</h3>
                  <p className="text-xs text-stone-400">Doğrudan mesaj gönderin</p>
                </div>
              </div>
              <ContactForm accommodation={accommodation} />
            </div>

            {/* Back to list */}
            <Link to="/accommodations"
              className="flex items-center justify-center gap-2 w-full py-3 border border-stone-200 text-stone-600 text-sm font-medium rounded-xl hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-arrow-left-line"></i>
              Tüm İşletmelere Dön
            </Link>
          </div>
        </div>
      </div>

      {/* Reservation Modal */}
      {reservationOpen && (
        <ReservationModal accommodation={accommodation} onClose={() => setReservationOpen(false)} />
      )}

      {/* Lightbox */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={() => setLightboxOpen(false)}>
          <button className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center text-white bg-white/10 hover:bg-white/20 rounded-full cursor-pointer transition-colors"
            onClick={() => setLightboxOpen(false)}>
            <i className="ri-close-line text-xl"></i>
          </button>
          <button className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full cursor-pointer transition-colors"
            onClick={(e) => { e.stopPropagation(); handlePrev(); }}>
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>
          <div className="max-w-5xl w-full px-16" onClick={(e) => e.stopPropagation()}>
            <div className="w-full h-[75vh]">
              <img src={allImages[activeImg]} alt={`${accommodation.businessName} ${activeImg + 1}`}
                className="w-full h-full object-contain" />
            </div>
            <div className="text-center mt-3 text-white/60 text-sm">
              {accommodation.businessName} — {activeImg + 1}/{allImages.length}
            </div>
            <div className="flex justify-center gap-2 mt-3 flex-wrap">
              {allImages.map((img, idx) => (
                <button key={idx} onClick={() => setActiveImg(idx)}
                  className={`w-14 h-10 rounded overflow-hidden border-2 cursor-pointer transition-all ${
                    activeImg === idx ? "border-amber-400" : "border-transparent opacity-50 hover:opacity-80"
                  }`}>
                  <img src={img} alt="" className="w-full h-full object-cover object-top" />
                </button>
              ))}
            </div>
          </div>
          <button className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full cursor-pointer transition-colors"
            onClick={(e) => { e.stopPropagation(); handleNext(); }}>
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AccommodationDetail;
